import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@theraply/api-client';

export interface CreateBookingRequest {
  therapistId: number;
  slotDate: string;  // ISO format YYYY-MM-DD
  slotTime: string;  // ISO format HH:MM
  sessionType: 'INTAKE' | 'REGULAR' | 'CRISIS';
  notes?: string;
}

export interface CreateBookingResponse {
  bookingId: number;
  clientSecret: string;
  amount: number;
  platformFee: number;
  therapistAmount: number;
  status: string;
}

export interface Booking {
  id: number;
  consumerId: number;
  therapistId: number;
  slotDate: string;
  slotTime: string;
  sessionType: 'INTAKE' | 'REGULAR' | 'CRISIS';
  status: 'PENDING_PAYMENT' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  amountCents: number;
  platformFeeCents: number;
  therapistAmountCents: number;
  notes?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CancelBookingRequest {
  reason?: string;
}

/**
 * Hook to create a new booking
 * Returns the booking ID and Stripe client secret for payment
 */
export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateBookingRequest): Promise<CreateBookingResponse> => {
      const response = await apiClient.post('/bookings', request);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate consumer bookings list
      queryClient.invalidateQueries({ queryKey: ['consumer-bookings'] });
    },
  });
}

/**
 * Hook to confirm a booking after successful payment
 */
export function useConfirmBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId: number): Promise<Booking> => {
      const response = await apiClient.post(`/bookings/${bookingId}/confirm`);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate both the specific booking and the bookings list
      queryClient.invalidateQueries({ queryKey: ['booking', data.id] });
      queryClient.invalidateQueries({ queryKey: ['consumer-bookings'] });
    },
  });
}

/**
 * Hook to fetch a specific booking by ID
 */
export function useBooking(bookingId: number | null) {
  return useQuery({
    queryKey: ['booking', bookingId],
    queryFn: async (): Promise<Booking> => {
      if (!bookingId) {
        throw new Error('Booking ID is required');
      }
      const response = await apiClient.get(`/bookings/${bookingId}`);
      return response.data;
    },
    enabled: !!bookingId,
  });
}

/**
 * Hook to fetch all bookings for the authenticated consumer
 */
export function useConsumerBookings() {
  return useQuery({
    queryKey: ['consumer-bookings'],
    queryFn: async (): Promise<{ bookings: Booking[]; total: number }> => {
      const response = await apiClient.get('/bookings/consumer/my-bookings');
      return response.data;
    },
  });
}

/**
 * Hook to cancel a booking
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bookingId,
      reason,
    }: {
      bookingId: number;
      reason?: string;
    }): Promise<{ id: number; status: string; cancelledAt: string; refunded: boolean }> => {
      const response = await apiClient.post(`/bookings/${bookingId}/cancel`, {
        reason: reason || 'Cancelled by user',
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate both the specific booking and the bookings list
      queryClient.invalidateQueries({ queryKey: ['booking', data.id] });
      queryClient.invalidateQueries({ queryKey: ['consumer-bookings'] });
    },
  });
}

/**
 * Hook to check slot availability before booking
 */
export function useCheckSlotAvailability() {
  return useMutation({
    mutationFn: async ({
      therapistId,
      slotDate,
      slotTime,
    }: {
      therapistId: number;
      slotDate: string;
      slotTime: string;
    }): Promise<boolean> => {
      // This would call a dedicated availability check endpoint
      // For now, we'll validate client-side and rely on server validation during booking
      return true;
    },
  });
}
